import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Modal } from '@/shared/ui/Modal'
import { Spinner } from '@/shared/ui/Spinner'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { toast } from '@/store/useToastStore'
import { useWithdrawals, useApproveWithdrawal, useRejectWithdrawal } from '@/api/hooks'
import type { WithdrawalRequest, WithdrawalStatus } from '@/api/services/withdrawalService'
import { cn } from '@/shared/utils'
import { Eye, Check, X, Landmark, ArrowRight } from 'lucide-react'

const TABS: { value: WithdrawalStatus | 'all'; labelKey: string }[] = [
  { value: 'all',       labelKey: 'withdrawals.tabAll' },
  { value: 'pending',   labelKey: 'withdrawals.tabPending' },
  { value: 'completed', labelKey: 'withdrawals.tabCompleted' },
  { value: 'cancelled', labelKey: 'withdrawals.tabCancelled' },
]

function statusCls(status: WithdrawalStatus) {
  if (status === 'completed') return 'bg-green-100 text-green-700'
  if (status === 'cancelled') return 'bg-red-100 text-red-600'
  return 'bg-brand-100 text-brand-700'
}

function formatDateTime(value?: string | null) {
  if (!value) return '—'
  // The API sends "2026-09-20 22:42:53" — a space, not the "T" ISO parsing
  // needs — so `new Date(value)` as-is returns Invalid Date in Safari/Firefox.
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('en-GB').replace(/\//g, '-')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}`
}

export function WithdrawalRequestsPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<WithdrawalStatus | 'all'>('pending')
  const [detailTarget, setDetailTarget] = useState<WithdrawalRequest | null>(null)
  const [approveTarget, setApproveTarget] = useState<WithdrawalRequest | null>(null)
  const [adminNotes, setAdminNotes] = useState('')
  const [rejectTarget, setRejectTarget] = useState<WithdrawalRequest | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectNotes, setRejectNotes] = useState('')

  const { data, isLoading, isError } = useWithdrawals(tab === 'all' ? undefined : tab)
  const rows: WithdrawalRequest[] = data ?? []

  const approve = useApproveWithdrawal()
  const reject = useRejectWithdrawal()

  const counts = useMemo(() => {
    const all = data ?? []
    return { pending: all.filter(r => r.status === 'pending').length }
  }, [data])

  const openApprove = (r: WithdrawalRequest) => { setAdminNotes(''); setApproveTarget(r) }

  const confirmApprove = async () => {
    if (!approveTarget) return
    try {
      await approve.mutateAsync({ id: approveTarget.id, admin_notes: adminNotes.trim() || undefined })
      toast.success(t('withdrawals.approveSuccess'))
      setApproveTarget(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('withdrawals.approveError'))
    }
  }

  const openReject = (r: WithdrawalRequest) => { setRejectReason(''); setRejectNotes(''); setRejectTarget(r) }

  const confirmReject = async () => {
    if (!rejectTarget) return
    if (!rejectReason.trim()) { toast.error(t('withdrawals.reasonRequiredError')); return }
    try {
      await reject.mutateAsync({ id: rejectTarget.id, reason: rejectReason.trim(), admin_notes: rejectNotes.trim() || undefined })
      toast.success(t('withdrawals.rejectSuccess'))
      setRejectTarget(null)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('withdrawals.rejectError'))
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('withdrawals.title')}
        subtitle={t('withdrawals.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), to: '/dashboard' }, { label: t('withdrawals.title') }]}
      />

      {/* ── Status tabs ── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
        {TABS.map(tb => (
          <button key={tb.value} onClick={() => setTab(tb.value)}
            className={cn('flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold border whitespace-nowrap transition-colors',
              tab === tb.value ? 'bg-brand-500 text-white border-brand-500' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300')}>
            {t(tb.labelKey)}
            {tb.value === 'pending' && counts.pending > 0 && (
              <span className={cn('text-xs font-bold rounded-full px-1.5 py-0.5 tabular-nums',
                tab === 'pending' ? 'bg-white/25' : 'bg-brand-100 text-brand-700')}>
                {counts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      <Card padding={false}>
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="w-8 h-8"/></div>
        ) : isError ? (
          <div className="text-center text-sm text-red-500 py-16">{t('withdrawals.loadError')}</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-16">{t('withdrawals.emptyState')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('withdrawals.colUser')}</th>
                  <th>{t('withdrawals.colBank')}</th>
                  <th>{t('withdrawals.colAmount')}</th>
                  <th>{t('withdrawals.colStatus')}</th>
                  <th>{t('withdrawals.colRequested')}</th>
                  <th className="text-end">{t('withdrawals.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="cursor-pointer" onClick={() => setDetailTarget(r)}>
                    <td>
                      <div className="font-semibold text-gray-800">{r.user_name || `#${r.user_id}`}</div>
                      <div className="text-xs text-gray-400">{r.user_email}</div>
                      <div className="text-xs text-gray-400">{r.user_phone}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                        <Landmark size={12} className="text-gray-400"/>{r.bank_name || '—'}
                      </div>
                      <div className="text-xs text-gray-400">{r.account_holder}</div>
                    </td>
                    <td><DirhamAmount value={r.amount} size={13}/></td>
                    <td><span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap', statusCls(r.status))}>{t(`withdrawals.status.${r.status}`)}</span></td>
                    <td className="text-gray-600 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                    <td className="text-end">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setDetailTarget(r) }}
                          title={t('withdrawals.viewDetails')}
                          aria-label={t('withdrawals.viewDetails')}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 bg-white hover:bg-gray-50"
                        >
                          <Eye size={11}/>{t('withdrawals.viewDetails')}
                        </button>
                        {r.status === 'pending' && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openApprove(r) }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-green-500 text-white hover:bg-green-600"
                            >
                              <Check size={11}/>{t('withdrawals.approve')}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openReject(r) }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100"
                            >
                              <X size={11}/>{t('withdrawals.reject')}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Detail modal ── */}
      <Modal open={!!detailTarget} onClose={() => setDetailTarget(null)} title={t('withdrawals.detailsTitle')} size="md">
        {detailTarget && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-gray-800">{detailTarget.user_name || `#${detailTarget.user_id}`}</div>
                <div className="text-xs text-gray-400">{detailTarget.user_email} · {detailTarget.user_phone}</div>
              </div>
              <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold', statusCls(detailTarget.status))}>{t(`withdrawals.status.${detailTarget.status}`)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('withdrawals.colAmount')}</div>
                <DirhamAmount value={detailTarget.amount} size={14} className="mt-1"/>
              </div>
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('withdrawals.transferId')}</div>
                <div className="text-xs font-mono font-semibold text-gray-800 mt-1 break-all">{detailTarget.transfer_id || '—'}</div>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50 space-y-2">
              <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('withdrawals.colBank')}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-gray-400">{t('withdrawals.bankName')}: </span><span className="font-semibold text-gray-800">{detailTarget.bank_name || '—'}</span></div>
                <div><span className="text-gray-400">{t('withdrawals.accountHolder')}: </span><span className="font-semibold text-gray-800">{detailTarget.account_holder || '—'}</span></div>
                <div><span className="text-gray-400">{t('withdrawals.accountNumber')}: </span><span className="font-mono font-semibold text-gray-800">{detailTarget.account_number || '—'}</span></div>
                <div><span className="text-gray-400">{t('withdrawals.iban')}: </span><span className="font-mono font-semibold text-gray-800">{detailTarget.iban || '—'}</span></div>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-gray-50">
              <div className="text-[11px] text-gray-400 uppercase font-semibold mb-1.5">{t('withdrawals.balanceChange')}</div>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <DirhamAmount value={detailTarget.balance_before} size={12}/>
                <ArrowRight size={13} className="text-gray-400"/>
                <DirhamAmount value={detailTarget.balance_after} size={12}/>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-gray-400">{t('withdrawals.requestedAt')}: </span><span className="font-semibold text-gray-700">{formatDateTime(detailTarget.created_at)}</span></div>
              <div><span className="text-gray-400">{t('withdrawals.completedAt')}: </span><span className="font-semibold text-gray-700">{formatDateTime(detailTarget.completed_at)}</span></div>
              {detailTarget.estimated_arrival && (
                <div className="col-span-2"><span className="text-gray-400">{t('withdrawals.estimatedArrival')}: </span><span className="font-semibold text-gray-700">{formatDateTime(detailTarget.estimated_arrival)}</span></div>
              )}
            </div>

            {(detailTarget.admin_notes || detailTarget.error_message) && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800">
                {detailTarget.admin_notes && <div><span className="font-semibold">{t('withdrawals.adminNotes')}: </span>{detailTarget.admin_notes}</div>}
                {detailTarget.error_message && <div className="mt-1"><span className="font-semibold">{t('withdrawals.errorMessage')}: </span>{detailTarget.error_message}</div>}
              </div>
            )}

            {detailTarget.status === 'pending' && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setDetailTarget(null); openReject(detailTarget) }}
                  className="py-2 rounded-lg border-2 border-red-300 text-red-600 hover:bg-red-50 text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  <X size={14}/>{t('withdrawals.reject')}
                </button>
                <button
                  onClick={() => { setDetailTarget(null); openApprove(detailTarget) }}
                  className="py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                >
                  <Check size={14}/>{t('withdrawals.approve')}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Approve modal ── */}
      <Modal open={!!approveTarget} onClose={() => setApproveTarget(null)} title={t('withdrawals.approveTitle')} size="sm">
        {approveTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('withdrawals.approveConfirm', { name: approveTarget.user_name || `#${approveTarget.user_id}` })}
            </p>
            <div className="p-3 rounded-xl bg-gray-50 flex items-center justify-between text-sm">
              <span className="text-gray-500">{t('withdrawals.colAmount')}</span>
              <DirhamAmount value={approveTarget.amount} size={13}/>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t('withdrawals.adminNotesOptional')}</label>
              <textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                rows={3}
                placeholder={t('withdrawals.adminNotesPlaceholder')}
                className="w-full text-sm border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30 resize-none"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setApproveTarget(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('common.cancel')}</button>
              <button
                onClick={confirmApprove}
                disabled={approve.isPending}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-green-500 hover:bg-green-600 disabled:opacity-50"
              >
                {approve.isPending ? t('withdrawals.approving') : t('withdrawals.approve')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Reject modal ── */}
      <Modal open={!!rejectTarget} onClose={() => setRejectTarget(null)} title={t('withdrawals.rejectTitle')} size="sm">
        {rejectTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('withdrawals.rejectConfirm', { name: rejectTarget.user_name || `#${rejectTarget.user_id}` })}
            </p>
            <div className="p-3 rounded-xl bg-gray-50 flex items-center justify-between text-sm">
              <span className="text-gray-500">{t('withdrawals.colAmount')}</span>
              <DirhamAmount value={rejectTarget.amount} size={13}/>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t('withdrawals.reasonRequired')} <span className="text-red-500">*</span></label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={2}
                placeholder={t('withdrawals.reasonPlaceholder')}
                className="w-full text-sm border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30 resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">{t('withdrawals.adminNotesOptional')}</label>
              <textarea
                value={rejectNotes}
                onChange={e => setRejectNotes(e.target.value)}
                rows={2}
                placeholder={t('withdrawals.adminNotesPlaceholder')}
                className="w-full text-sm border border-gray-200 rounded-lg p-2.5 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30 resize-none"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setRejectTarget(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{t('common.cancel')}</button>
              <button
                onClick={confirmReject}
                disabled={reject.isPending}
                className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-red-500 hover:bg-red-600 disabled:opacity-50"
              >
                {reject.isPending ? t('withdrawals.rejecting') : t('withdrawals.reject')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
